/**
 * LoginPage — sign-in / register screen for the dashboard.
 * Toggles between login and register modes. Both call auth_server.py.
 *
 * Self-registration always creates a 'user' role account.
 * Admin accounts can only be created via seed_admin.py CLI.
 */
import React, { useState } from 'react';
import { useAuth } from './AuthContext';
import './LoginPage.css';

type Mode = 'login' | 'register';

export default function LoginPage() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState<Mode>('login');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);

  const switchMode = (next: Mode) => {
    setMode(next);
    setError('');
    setNotice('');
    setPassword('');
    setConfirmPw('');
    setShowPw(false);
    setShowConfirmPw(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setNotice('');

    if (!username || !password) {
      setError('Please enter both username and password');
      return;
    }

    if (mode === 'register') {
      if (password.length < 8) {
        setError('Password must be at least 8 characters');
        return;
      }
      if (password !== confirmPw) {
        setError('Passwords do not match');
        return;
      }
      if (!/^[a-zA-Z0-9_]{3,32}$/.test(username.trim())) {
        setError(
          'Username must be 3–32 characters, letters/numbers/underscore only',
        );
        return;
      }
    }

    setLoading(true);
    const result =
      mode === 'login'
        ? await login(username, password)
        : await register(username, password);
    setLoading(false);

    if (!result.ok) {
      setError(result.error);
      return;
    }
    // Registration lands in a "pending approval" state — no auto-login.
    // Show the notice and switch back to the sign-in form. (When mode is
    // 'register' the result is always the register() shape.)
    if (mode === 'register') {
      const reg = result as { ok: true; pending: boolean; message: string };
      if (reg.pending) {
        setNotice(reg.message);
        setPassword('');
        setConfirmPw('');
        setMode('login');
      }
    }
  };

  const isRegister = mode === 'register';

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-header">
          <h1>Trading Dashboard</h1>
          <p>{isRegister ? 'Create a new account' : 'Sign in to continue'}</p>
        </div>

        <form className="login-form" onSubmit={handleSubmit}>
          <div className="login-field">
            <label htmlFor="login-username">Username</label>
            <input
              id="login-username"
              type="text"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder={isRegister ? 'Choose a username' : 'Username'}
              autoFocus
            />
          </div>

          <div className="login-field">
            <label htmlFor="login-password">Password</label>
            <div className="login-pw-wrap">
              <input
                id="login-password"
                type={showPw ? 'text' : 'password'}
                autoComplete={isRegister ? 'new-password' : 'current-password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={isRegister ? 'At least 8 characters' : '••••••••'}
              />
              <button
                type="button"
                className="login-pw-toggle"
                onClick={() => setShowPw((v) => !v)}
                tabIndex={-1}
                aria-label={showPw ? 'Hide password' : 'Show password'}
              >
                {showPw ? '◡' : '⊙'}
              </button>
            </div>
          </div>

          {isRegister && (
            <div className="login-field">
              <label htmlFor="login-confirm">Confirm Password</label>
              <div className="login-pw-wrap">
                <input
                  id="login-confirm"
                  type={showConfirmPw ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={confirmPw}
                  onChange={(e) => setConfirmPw(e.target.value)}
                  placeholder="Re-enter password"
                />
                <button
                  type="button"
                  className="login-pw-toggle"
                  onClick={() => setShowConfirmPw((v) => !v)}
                  tabIndex={-1}
                  aria-label={showConfirmPw ? 'Hide password' : 'Show password'}
                >
                  {showConfirmPw ? '◡' : '⊙'}
                </button>
              </div>
            </div>
          )}

          {notice && (
            <div
              className="login-notice"
              style={{
                color: '#1a7f37',
                background: '#e6f4ea',
                border: '1px solid #1a7f37',
                borderRadius: 6,
                padding: '8px 10px',
                fontSize: 13,
                marginBottom: 8,
              }}
            >
              {notice}
            </div>
          )}
          {error && <div className="login-error">{error}</div>}

          <button type="submit" className="login-submit" disabled={loading}>
            {loading
              ? isRegister
                ? 'Creating account…'
                : 'Signing in…'
              : isRegister
                ? 'Create Account'
                : 'Sign In'}
          </button>
        </form>

        <div className="login-switch">
          {isRegister ? (
            <>
              Already have an account?{' '}
              <button
                type="button"
                className="login-switch-btn"
                onClick={() => switchMode('login')}
              >
                Sign in
              </button>
            </>
          ) : (
            <>
              Don't have an account?{' '}
              <button
                type="button"
                className="login-switch-btn"
                onClick={() => switchMode('register')}
              >
                Register
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
